import { isAxiosError } from 'axios';

/**
 * Returns true when `err` is a request-cancellation error — the native
 * AbortError or axios CanceledError.
 */
function isAbortError(err: unknown): boolean {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define -- v5 parser false-positive
  const name = (err as { name?: string })?.name;
  return name === 'AbortError' || name === 'CanceledError';
}

// True while this document is on its way out (a full navigation, reload or tab
// close). Requests killed by the browser at that point surface as ERR_NETWORK —
// no response, and NOT a CanceledError — indistinguishable by shape from a real
// connectivity failure. They are cancellations in spirit: the user is leaving,
// nothing is wrong with the connection, and a "Connection Failed" toast flashed
// over a page that is being torn down is pure noise.
let documentUnloading = false;

/**
 * Marks the document as unloading so isNetworkError stops treating
 * browser-killed requests as connectivity failures. Called explicitly right
 * before a deliberate `window.location` navigation (the SSO sign-out redirect
 * in AuthStore.workerLogout) — deterministic, no reliance on event ordering.
 */
export function markDocumentUnloading(): void {
  documentUnloading = true;
}

/**
 * Clears the unloading mark. Used when a marked-as-leaving document turns out
 * to still be alive — a bfcache restore (pageshow with persisted=true) — and by
 * tests to isolate the module state.
 */
export function unmarkDocumentUnloading(): void {
  documentUnloading = false;
}

/**
 * Installs the document-lifetime listeners that keep the unloading mark honest
 * for departures nothing announces explicitly: reload, tab close, any external
 * navigation. `pagehide` sets the mark; `pageshow` with `persisted` clears it,
 * because a bfcache-restored page is alive again and real connectivity failures
 * must surface. Called once at bootstrap (src/index.js); returns a cleanup for
 * tests.
 */
export function watchDocumentUnloading(target: Window = window): () => void {
  const onPageHide = () => markDocumentUnloading();
  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) {
      unmarkDocumentUnloading();
    }
  };

  target.addEventListener('pagehide', onPageHide);
  target.addEventListener('pageshow', onPageShow);

  return () => {
    target.removeEventListener('pagehide', onPageHide);
    target.removeEventListener('pageshow', onPageShow);
  };
}

/**
 * Returns true when `err` is a network/connectivity failure — an AXIOS error
 * whose request never reached a response (offline, DNS failure, connection
 * refused, timeout) — as opposed to a server error that came back with an HTTP
 * status. Cancellations are excluded (they are not connectivity failures), and
 * so is everything that dies while the document itself is unloading — the
 * browser kills those requests with the same ERR_NETWORK shape, but the
 * "failure" is just the page leaving.
 *
 * The axios check is load-bearing, not a nicety (VFM-3525 lesson): the
 * previous `!err.response` alone also matched every NON-axios exception — a
 * TypeError thrown by a response mapper inside `.then(...)` has no `response`
 * either, so a wire-contract mismatch surfaced as the "Connection Failed"
 * toast instead of the flow's own error state, masking the real bug. Only
 * axios itself can produce a genuine connectivity failure; anything else
 * without a response is a programming error and must fall through to the
 * caller's error branch.
 *
 * Used to decide whether to surface a "lost connection" notification for a
 * failed data load (VA-3396 and the KB list/tree loads).
 */
export function isNetworkError(err: unknown): boolean {
  if (documentUnloading || isAbortError(err)) {
    return false;
  }
  return isAxiosError(err) && !err.response;
}
